import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import {
  createVehicle,
  updateVehicle,
  getVehicleById,
} from "../../../service/securityService";
import { Vehicle, VehicleFormData, VehicleOwnerType } from "../../../types/security_types";
import axiosInstance from "../../../common/axiosInstance";
import {
  normalizeVehiclePlate,
  isValidVehiclePlateFormat,
} from "./parkingPlateUtils";

interface VehicleFormProps {
  vehicle?: Vehicle;
  onSuccess: () => void;
  onCancel: () => void;
}

const VehicleForm: React.FC<VehicleFormProps> = ({
  vehicle,
  onSuccess,
  onCancel,
}) => {
  const isEdit = !!vehicle;
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [zones, setZones] = useState<Array<{ value: string; label: string }>>([]);

  const [formData, setFormData] = useState<VehicleFormData>({
    vehicleNumber: "",
    ownerType: "Staff",
    ownerName: "",
    ownerId: "",
    parkingZone: "",
    parkingZoneId: "",
    validityStart: "",
    validityEnd: "",
  });

  useEffect(() => {
    fetchZones();
    if (isEdit && vehicle) {
      loadVehicleData();
    }
  }, [vehicle]);

  const fetchZones = async () => {
    try {
      const response = await axiosInstance.get("/api/security/access-zones");
      if (response.data.success) {
        const zonesData = response.data.data || response.data.message || [];
        setZones(
          zonesData.map((zone: any) => ({
            value: zone._id || zone.id,
            label: zone.zoneName,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching zones:", error);
    }
  };

  const loadVehicleData = async () => {
    if (!vehicle) return;
    try {
      setLoading(true);
      const id = vehicle._id || vehicle.id;
      if (!id) return;
      const vehicleData = await getVehicleById(id);
      setFormData({
        vehicleNumber: vehicleData.vehicleNumber || "",
        ownerType: vehicleData.ownerType || "Staff",
        ownerName: vehicleData.ownerName || "",
        ownerId: vehicleData.ownerId || "",
        parkingZone: vehicleData.parkingZone || "",
        parkingZoneId: vehicleData.parkingZoneId || "",
        validityStart: vehicleData.validityStart
          ? new Date(vehicleData.validityStart).toISOString().slice(0, 10)
          : "",
        validityEnd: vehicleData.validityEnd
          ? new Date(vehicleData.validityEnd).toISOString().slice(0, 10)
          : "",
      });
    } catch (error: any) {
      console.error("Error loading vehicle data:", error);
      toast.error("Failed to load vehicle data");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    const normalized = normalizeVehiclePlate(formData.vehicleNumber);

    if (!normalized) {
      newErrors.vehicleNumber = "Vehicle number is required";
    } else if (!isValidVehiclePlateFormat(normalized)) {
      newErrors.vehicleNumber =
        "Use 6–12 letters or digits (e.g. KA01AB1234). Spaces/dashes are removed automatically.";
    }

    if (!formData.ownerName.trim()) {
      newErrors.ownerName = "Owner name is required";
    }

    if (!formData.parkingZoneId?.trim()) {
      newErrors.parkingZone = "Parking zone is required";
    }

    if (!formData.validityStart?.trim()) {
      newErrors.validityStart = "Validity start is required";
    }
    if (!formData.validityEnd?.trim()) {
      newErrors.validityEnd = "Validity end is required";
    }
    if (
      formData.validityStart &&
      formData.validityEnd &&
      new Date(formData.validityEnd) <= new Date(formData.validityStart)
    ) {
      newErrors.validityEnd = "End date must be after start date";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    try {
      setSubmitting(true);
      const submitData = {
        ...formData,
        vehicleNumber: normalizeVehiclePlate(formData.vehicleNumber),
        validityStart: formData.validityStart
          ? new Date(formData.validityStart).toISOString()
          : undefined,
        validityEnd: formData.validityEnd
          ? new Date(formData.validityEnd).toISOString()
          : undefined,
      };

      if (isEdit && vehicle) {
        const id = vehicle._id || vehicle.id;
        if (!id) return;
        await updateVehicle(id, submitData);
        toast.success("Vehicle updated successfully");
      } else {
        await createVehicle(submitData);
        toast.success("Vehicle registered successfully");
      }
      onSuccess();
    } catch (error: any) {
      console.error("Error saving vehicle:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.resMessage ||
          "Failed to save vehicle"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <>
      <h2 className="text-xl font-semibold text-gray-800 border-b px-6 pb-2 mb-4">
        {isEdit ? "Update Vehicle Details" : "Create New Vehicle"}
      </h2>
      <form onSubmit={handleSubmit} id="vehicleForm" className="px-6 pb-6">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Vehicle Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.vehicleNumber}
              onChange={(e) => {
                setFormData({ ...formData, vehicleNumber: e.target.value.toUpperCase() });
                setErrors({ ...errors, vehicleNumber: "" });
              }}
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] ${
                errors.vehicleNumber ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter vehicle number"
              maxLength={20}
              required
            />
            <p className="text-xs text-gray-500">
              Normalized to 6–12 alphanumeric characters (typical number plate).
            </p>
            {errors.vehicleNumber && (
              <span className="text-red-500 text-xs">{errors.vehicleNumber}</span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Owner Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.ownerType}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    ownerType: e.target.value as VehicleOwnerType,
                  });
                }}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                required
              >
                <option value="Staff">Staff</option>
                <option value="Visitor">Visitor</option>
                <option value="Vendor">Vendor</option>
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Owner Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.ownerName}
                onChange={(e) => {
                  setFormData({ ...formData, ownerName: e.target.value });
                  setErrors({ ...errors, ownerName: "" });
                }}
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] ${
                errors.ownerName ? "border-red-500" : "border-gray-300"
              }`}
                placeholder="Enter owner name"
                required
              />
              {errors.ownerName && (
                <span className="text-red-500 text-xs">{errors.ownerName}</span>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Parking Zone <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.parkingZoneId || ""}
              onChange={(e) => {
                const selectedZone = zones.find((z) => z.value === e.target.value);
                setFormData({
                  ...formData,
                  parkingZoneId: e.target.value || undefined,
                  parkingZone: selectedZone?.label || "",
                });
                setErrors({ ...errors, parkingZone: "" });
              }}
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] ${
                errors.parkingZone ? "border-red-500" : "border-gray-300"
              }`}
              required
            >
              <option value="">Select parking zone</option>
              {zones.map((zone) => (
                <option key={zone.value} value={zone.value}>
                  {zone.label}
                </option>
              ))}
            </select>
            {errors.parkingZone && (
              <span className="text-red-500 text-xs">{errors.parkingZone}</span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Validity start <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.validityStart}
                onChange={(e) => {
                  setFormData({ ...formData, validityStart: e.target.value });
                  setErrors({ ...errors, validityEnd: "", validityStart: "" });
                }}
                className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] ${
                  errors.validityStart ? "border-red-500" : "border-gray-300"
                }`}
                required
              />
              {errors.validityStart && (
                <span className="text-red-500 text-xs">{errors.validityStart}</span>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Validity end <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.validityEnd}
                onChange={(e) => {
                  setFormData({ ...formData, validityEnd: e.target.value });
                  setErrors({ ...errors, validityEnd: "" });
                }}
                className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] ${
                  errors.validityEnd ? "border-red-500" : "border-gray-300"
                }`}
                required
              />
              {errors.validityEnd && (
                <span className="text-red-500 text-xs">{errors.validityEnd}</span>
              )}
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default VehicleForm;
